/**
 * 공통 프런트엔드 엔트리.
 * 슬롯 고유 스크립트는 services/serviceN/static_src/app.ts 에 두면
 * 빌드 스크립트가 함께 번들한다.
 *
 * 주의: Zynix 셸 페이지에서는 Zynix 의 main.js/preline/defaultmenu 가 이미
 * 메뉴·드롭다운을 처리한다. 여기서는 우리 기능만 붙인다.
 */
import { initReveal } from './reveal';
import { initKeepScroll, initReturnScroll } from './scroll';
import { initSessionGuard } from './session';
import { initZynixGlue } from './zynix_glue';
import { initRtable } from './rtable';
import { initList } from './list';
import { initHScroll } from './hscroll';
import * as modal from './modal';
/* D001 — 시스템 창을 대신하는 공용 대화상자. 템플릿 안 인라인 스크립트도
   부를 수 있게 `nx.dlg` 로 내놓는다(부품 한 벌 — 0-0). */
import { dlgAlert, dlgBusy, dlgConfirm, dlgPrompt } from './dlg';
import { NxEvents } from './events';
import { initTree, tree } from './tree';
import { initDoxIcon } from './dox_icon';
import { initDoxShare } from './dox_share';
import { initDoxPw } from './dox_pw';
import { pdfPageCount, pdfPageCountHead } from './pdf_pages';
import { initLocalTime } from './localtime';

/**
 * `window.nx` 의 타입은 **여기 한 곳**에만 적는다.
 *
 * ★ `nx?` — 물음표가 붙어 있다. `boot()` 가 돌기 전에는 정말로 없기 때문이다.
 *   예전에는 필수로 적어 두어서, 다른 모듈들이 `window.nx` 를 쓰려면
 *   `(window as any).nx?.…` 로 **타입을 통째로 버려야** 했다(12군데였다).
 *   그러면 오타(`nx.modal.oepn`)를 컴파일러가 잡아 주지 못한다.
 *   물음표를 붙이면 `window.nx?.modal?.open(...)` 이 타입이 살아 있는 채로 된다.
 *
 * ★ `modal`/`tree` 를 `typeof` 로 적지 않고 **필요한 모양만** 적는다.
 *   `typeof tree` 로 적으면 tree.ts 가 `window.nx` 를 쓰는 순간
 *   "자기 자신을 참조한다" 는 순환이 된다.
 */
declare global {
  interface Window {
    nx?: {
      events: typeof NxEvents;
      /** 공통 팝업. nx.modal.open(url, {size,title,context}) → Promise */
      modal: {
        open(
          url: string,
          opts?: {
            size?: 'sheet' | 'inset' | 'full';
            title?: string;
            context?: Record<string, unknown>;
            history?: boolean;
          },
        ): Promise<unknown>;
        done(data: unknown): void;
        close(): void;
        /** ★ F796 — **지금 떠 있는 팝업의 내용만** 다른 주소로 바꾼다.
         *  히스토리를 안 만진다(닫았다 다시 열면 칸이 어긋난다 — modal.ts
         *  의 `goto` 주석에 실측이 있다). 팝업이 없으면 false. */
        goto(url: string): boolean;
        /** 브라우저 경고창 대신 쓰는 알림 팝업. 확인 버튼 하나. */
        alert(message: string, opts?: { title?: string }): Promise<unknown>;
        /** F504: 브라우저 확인창 대신 쓰는 확인 팝업. 「예」면 true,
         *  [닫기]/Esc/뒤로가기면 null(= 하지 않는다). */
        confirm(message: string,
                opts?: { title?: string; okLabel?: string }): Promise<unknown>;
        /** F154: 브라우저 입력창 대신 쓰는 이름 입력 팝업(문서 이름 수정과
         *  같은 모양). [저장] 이면 입력값, [닫기]/Esc 면 null. */
        prompt(label: string, value: string,
               opts?: { title?: string }): Promise<unknown>;
        /** F163: nx.modal 밖의 오버레이에 「뒤로가기 한 칸」을 붙인다.
         *  release() = 버튼으로 닫음(기록 되돌림) · release(true) = 뒤로가기로
         *  닫힘(되돌리지 않는다 — 또 부르면 진짜 이전 화면으로 나간다). */
        backCloser(onBack: () => void): { release(fromBack?: boolean): void };
      };
      /** D001 — 팝업 틀이 없는 자리에서 쓰는 **공용 대화상자**(시스템 창 금지).
       *  nx.modal 쪽이 있으면 그쪽을 먼저 쓴다 — 이건 마지막 창구다. */
      dlg: {
        alert(message: string, w?: { ok?: string; cancel?: string }): Promise<unknown>;
        confirm(message: string, w?: { ok?: string; cancel?: string }): Promise<unknown>;
        prompt(label: string, value: string,
               w?: { ok?: string; cancel?: string }): Promise<unknown>;
        /** ★ F651 — 누를 것이 없는 «알리는 판». 돌려주는 것은 **걷는 함수**다
         *  (스스로 사라지는 타이머가 없다 — 0-9 규칙 7). */
        busy(message: string, icon?: string): () => void;
      };
      /** 트리. nx.tree.move(id, parentId) 등 — 트리가 있는 화면에서만 의미 있다 */
      tree: typeof tree;
      /** 번역. nx.t('tree.error.max_depth', {max: 4}) — 서버 t() 의 JS 판 */
      t: (key: string, params?: Record<string, unknown>) => string;
      slot: string | null;
      lang: string | null;
      theme: string | null;
      /** CSRF 토큰. 상태를 바꾸는 fetch 는 `X-CSRF-Token` 으로 이걸 보낸다. */
      csrf: string | null;
      /** 상태를 바꾸는 fetch 에 붙일 헤더. `{...nx.csrfHeaders()}` 로 쓴다. */
      csrfHeaders: () => Record<string, string>;
      /** F181: PDF 쪽수 — 업로드 없이 로컬 파일에서(표시용 · 차감 정본은
       *  서버 pypdf). **null = 모름** — 틀린 숫자를 돌려주지 않는다.
       *  압축 객체 스트림 해제(DecompressionStream)가 비동기라 Promise 다. */
      pdfPages: (bytes: Uint8Array) => Promise<number | null>;
      /** F929 B-4: 앞 조각(선형화 사전)만으로 — 못 믿으면 null(그때 전체 판독). */
      pdfPagesHead: (head: Uint8Array, fileSize: number) => number | null;
    };
    /** Preline 컴포넌트의 전역. 셸이 preline 을 안 싣는 화면도 있어 선택적이다. */
    HSStaticMethods?: { autoInit?: () => void };
  }
}

/**
 * 화면 스크립트용 번역.
 *
 * 사전은 `partials/_i18n.html` 이 `<script type="application/json" id="nx-i18n">`
 * 로 내려보낸다(범위는 `core/nx/i18n.py` 의 JS_NAMESPACES).
 *
 * ★ 사전이 없으면 **키를 그대로 돌려준다** — 서버 `translate()` 와 같은 규칙이다.
 *   화면에 `tree.error.name_required` 가 보이면 그건 "다국어가 아직 안 됐다" 가
 *   아니라 **그 키가 사전에 없다**는 뜻이다. 바로 알아채라고 이렇게 둔다.
 */
function makeT(): (key: string, params?: Record<string, unknown>) => string {
  let dict: Record<string, string> = {};
  try {
    const el = document.getElementById('nx-i18n');
    if (el?.textContent) dict = JSON.parse(el.textContent);
  } catch {
    /* 사전이 깨져도 화면은 동작해야 한다 */
  }
  return (key, params) => {
    let s = dict[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      }
    }
    return s;
  };
}

/**
 * CSRF 토큰 — `partials/_locale_head.html` 이 `<meta name="csrf-token">` 으로 내려준다.
 *
 * ★ 폼은 이걸 안 쓴다. `ui/_ui.html` 의 form() 매크로가 숨은 필드로 넣는다.
 *   여기 있는 건 **fetch/XHR 전용**이다 — 본문이 JSON 이나 multipart 라
 *   서버가 본문을 뒤지지 않고 헤더만 본다(core/nx/csrf.py).
 */
function csrfToken(): string | null {
  const el = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
  return el?.content || null;
}

/**
 * 상태를 바꾸는 fetch 에 붙일 헤더.
 *
 * ★ 없으면 **빈 객체**를 돌려준다. 빈 값을 헤더로 보내면 서버가 "헤더는 왔는데
 *   토큰이 틀렸다" 로 읽어 폼 본문을 볼 기회를 잃는다.
 */
function csrfHeaders(): Record<string, string> {
  const t = csrfToken();
  return t ? { 'X-CSRF-Token': t } : {};
}

function boot(): void {
  const html = document.documentElement;
  const csrf = csrfToken();
  window.nx = {
    events: NxEvents,
    modal,
    dlg: { alert: dlgAlert, confirm: dlgConfirm, prompt: dlgPrompt,
           busy: dlgBusy },
    tree,
    t: makeT(),
    slot: document.body.dataset.slot ?? null,
    lang: html.getAttribute('lang'),
    theme: html.classList.contains('dark') ? 'dark' : 'light',
    csrf,
    csrfHeaders,
    pdfPages: pdfPageCount,
    pdfPagesHead: pdfPageCountHead,
  };
  // 9-105: 부팅 단계를 **서로 격리**한다 — 한 단계가 던지면 그 뒤 단계가
  // 통째로 죽는 구조였다(사장님 실측: 상세 화면에서 표식(mark)이 안 남음
  // = initReturnScroll 이전 단계가 죽었을 때의 증상과 일치). 실패한 단계는
  // 콘솔에 이름과 원인을 크게 남긴다 — 다음 진단이 한 번에 끝난다.
  const step = (name: string, fn: () => void): void => {
    try {
      fn();
    } catch (e) {
      console.error('[nx] boot 실패:', name, e);
    }
  };
  // F221(2026-08-07): langswitch 단계는 없앴다 — 언어 스위처가
  // `<details>`(무 JS) 공용 조각으로 통일됐다(partials/_lang_menu.html).
  step('reveal', initReveal);
  // 고정 높이 스크롤 영역(트리·목록)의 자리를 새로고침 너머로 잇는다.
  step('keepscroll', initKeepScroll);
  // F197: [목록으로] 복귀 시 창 스크롤 유지(data-nx-return 링크).
  step('return-scroll', initReturnScroll);
  step('session', initSessionGuard);
  step('zynix', initZynixGlue);
  step('rtable', initRtable);   // F278: 반응형 표 라벨(전 표 공통)
  step('list', initList);       // dxx145-3: 목록 틀(높이·가로 스크롤·고정 열·팝업 행 갱신)
  step('hscroll', initHScroll); // 2026-10-21: 가로로 미는 줄·슬라이드의 오른쪽 «더 있음» 화살표(hscroll.ts)
  step('modal', () => modal.initModal());
  // 트리가 없는 화면에서는 즉시 반환한다(선택자 하나 값).
  step('tree', initTree);
  // F262: 문서 아이콘 업로드 — 위임이라 모달로 끼워지는 폼에도 듣는다.
  step('dox-icon', initDoxIcon);
  // 뷰링크(2026-08-08): [URL] 복사·[QR] 다운로드 — 위임.
  step('dox-share', initDoxShare);
  // F272: 공개 뷰 비밀번호 팝업의 행 추가/삭제 — 위임.
  step('dox-pw', initDoxPw);
  // F305: [data-nx-utc] → 브라우저 로컬 시간(사장님: "클라이언트 로컬").
  step('localtime', initLocalTime);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
